import { useState, useEffect } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { ShoppingCart, Menu, X, ArrowUpRight, UserRound } from "lucide-react";
import { useCart } from "@/context/CartContext";
import { PORTAL_URL } from "@/lib/site";

export const Logo = ({ compact = false }) => (
  <Link to="/" data-testid="logo-link" className="flex items-center gap-2.5 shrink-0">
    <img src="/assets/logo/emblem.png" alt="Regal Tuning logo" className="h-10 w-auto" />
    {!compact && (
      <span className="font-display text-lg font-extrabold tracking-[0.12em] leading-none text-ink">
        REGAL<span className="text-regal-amber">TUNING</span>
      </span>
    )}
  </Link>
);

const LINKS = [
  { to: "/", label: "Home" },
  { to: "/tuning-files", label: "Tuning Files" },
  { to: "/tuning-database", label: "Tuning Database" },
  { to: "/tuning-tools", label: "Tuning Tools" },
  { to: "/become-a-dealer", label: "Become a Dealer" },
  { to: "/about", label: "About" },
  { to: "/contact", label: "Contact" },
];

export default function Header() {
  const { count, setOpen } = useCart();
  const [mobile, setMobile] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const { pathname } = useLocation();

  useEffect(() => setMobile(false), [pathname]);
  useEffect(() => {
    const on = () => setScrolled(window.scrollY > 24);
    window.addEventListener("scroll", on, { passive: true });
    return () => window.removeEventListener("scroll", on);
  }, []);

  const navCls = ({ isActive }) =>
    `font-display text-[13px] font-semibold uppercase tracking-[0.14em] transition-colors duration-300 hover:text-regal-amber ${isActive ? "text-regal-amber" : "text-neutral-300"}`;

  return (
    <header data-testid="site-header" className={`fixed inset-x-0 top-0 z-50 transition-all duration-500 ${scrolled || mobile ? "glass" : "bg-transparent border-b border-transparent"}`}>
      <div className="container-regal flex h-16 sm:h-20 items-center justify-between gap-4">
        <Logo />
        <nav className="hidden lg:flex items-center gap-7" data-testid="desktop-nav">
          {LINKS.map((l) => <NavLink key={l.to} to={l.to} end={l.to === "/"} className={navCls} data-testid={`nav-${l.label.toLowerCase().replace(/\s+/g, "-")}`}>{l.label}</NavLink>)}
        </nav>
        <div className="flex items-center gap-2 sm:gap-3">
          <Link
            to="/account"
            data-testid="nav-account-link"
            aria-label="My account"
            className="grid h-11 w-11 place-items-center border border-line text-neutral-300 hover:border-regal-amber hover:text-regal-amber transition-colors duration-300"
          >
            <UserRound size={18} />
          </Link>
          <button
            onClick={() => setOpen(true)}
            data-testid="cart-open-button"
            aria-label="Open shopping cart"
            className="relative grid h-11 w-11 place-items-center border border-line text-neutral-300 hover:border-regal-amber hover:text-regal-amber transition-colors duration-300"
          >
            <ShoppingCart size={18} />
            {count > 0 && (
              <span data-testid="cart-count-badge" className="absolute -right-1.5 -top-1.5 grid h-5 min-w-5 place-items-center bg-regal-amber px-1 font-mono text-[10px] font-bold text-void">{count}</span>
            )}
          </button>
          <a
            href={PORTAL_URL}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="nav-file-portal-button"
            className="inline-flex items-center gap-1.5 bg-regal-amber px-3 sm:px-5 h-11 font-display text-[11px] sm:text-[13px] font-bold uppercase tracking-[0.14em] text-white hover:bg-amber-400 transition-colors duration-300"
          >
            <span className="hidden sm:inline">File&nbsp;</span>Portal <ArrowUpRight size={15} />
          </a>
          <button
            className="lg:hidden grid h-11 w-11 place-items-center border border-line text-neutral-200"
            onClick={() => setMobile((v) => !v)}
            aria-label="Toggle menu"
            data-testid="mobile-menu-button"
          >
            {mobile ? <X size={20} /> : <Menu size={20} />}
          </button>
        </div>
      </div>
      {mobile && (
        <div className="lg:hidden glass border-t border-line" data-testid="mobile-menu">
          <nav className="container-regal flex flex-col py-4">
            {LINKS.map((l) => (
              <NavLink key={l.to} to={l.to} end={l.to === "/"} data-testid={`mobile-nav-${l.label.toLowerCase().replace(/\s+/g, "-")}`} className={({ isActive }) => `py-3.5 font-display text-base font-semibold uppercase tracking-[0.14em] border-b border-line/60 ${isActive ? "text-regal-amber" : "text-neutral-200"}`}>
                {l.label}
              </NavLink>
            ))}
            <NavLink to="/account" data-testid="mobile-nav-account" className={({ isActive }) => `py-3.5 font-display text-base font-semibold uppercase tracking-[0.14em] border-b border-line/60 ${isActive ? "text-regal-amber" : "text-neutral-200"}`}>
              My Account
            </NavLink>
            <a href={PORTAL_URL} target="_blank" rel="noopener noreferrer" data-testid="mobile-file-portal-button" className="mt-4 mb-2 inline-flex items-center justify-center gap-2 bg-regal-amber py-4 font-display text-sm font-bold uppercase tracking-[0.16em] text-void">
              File Portal <ArrowUpRight size={16} />
            </a>
          </nav>
        </div>
      )}
    </header>
  );
}
